// ===========================================================================================================================================
// - Tab-scoped persistence (namespaced snapshot, debounce) — CreateTenderPage-style
// - Uses Tender DTO (Tender returns Markets/TransportTypes/Carriers/FCL/LCL/Phases)
// - UX hydrate snapshot with useLayoutEffect so content renders with data on first paint
// - Inline views (no heavyweight parents being mounted/unmounted)
// - **Persistence hardening**:
//     1) Namespaced Redux snapshot per tab (ex: "<tabId>::TFIN").
//     2) SessionStorage mirror per namespace (survives route unmounts / slice resets inside the session).
//     3) Aggressive flush on: tab switch, component unmount, and beforeunload (no data lost on navigation).
//     4) Debounced flush for normal state churn (reduce Redux noise).
// - Single source of truth for the page header label, tender data and view selection
// - **API update**:
//     1) Quotes are now loaded from a single endpoint: TenderQuotes (filter by TenderId + IsAccepted).
//     2) Lanes are now loaded from TenderLanes (filter by TenderId).
//     3) Distinct carriers come from TenderQuotes/distinctCarriers.
//     4) Lane metrics (best Bid/Loads/ETD) come from TenderQuotes/laneSummaries/accepted.
// -----------------------------------------------------------------------------------------------------------------------------------------------------------

import React, {
  useEffect,
  useLayoutEffect, // sync hydration before paint
  useState,
  useContext,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  LoadingContext,
  MHeader,
  MButton,
  BufferService,
  M_StateActions,
  NavigateContext,
  MSelect, // same styled select used by DynamicSelectBox
} from "src/M_Components";
import PropTypes from "prop-types";
import { isFailurePayload, apiMessage } from "src/components/utils/apiResponse";

import TopsByCarrierTable from "../../components/Tenders/TopsByCarrierTable";
import TopsByDestinationTable from "../../components/Tenders/TopsByDestinationTable";
import TopsBySourceTable from "../../components/Tenders/TopsBySourceTable";
import ViewLaneTable from "../../components/Tenders/ViewLaneTable";
import CarrierByLaneTable from "../../components/Tenders/carrierByLaneTable";
import TenderSummaryTable from "../../components/Tenders/TenderSummaryTable";

import "./TenderFinishedPage.css";

// ===========================================================================================================================================
// Helpers (pure + tiny)
// ===========================================================================================================================================

// stable namespace for this page
const stateKeyFrom = (tid) => (tid ? `${tid}::TFIN` : null);

// picks a value from [{key,value}] snapshot
const pickKV = (arr, k) => (arr || []).find((x) => x.key === k)?.value;

// normalizes API arrays
const norm = (data) =>
  Array.isArray(data?.Items)
    ? data.Items
    : Array.isArray(data)
      ? data
      : data || [];

// cheap deep equality for snapshot values
const deepEq = (a, b) => {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return a === b;
  }
};

// storage mirror per namespace
const ssKey = (ns) => `NS_SNAPSHOT:${ns}`;
const readFromStorage = (ns) => {
  if (!ns) return null;
  try {
    const raw = sessionStorage.getItem(ssKey(ns));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};
const writeToStorage = (ns, snapshotArray) => {
  if (!ns) return;
  try {
    sessionStorage.setItem(
      ssKey(ns),
      JSON.stringify(Array.isArray(snapshotArray) ? snapshotArray : [])
    );
  } catch {
    // storage may be unavailable/full — ignore; Redux snapshot still works
  }
};

// inline view helper model (used by the "default" dashboard)
// normalize carriers to a consistent shape
const normalizeCarriers = (carriers) =>
  (carriers || []).map((c) => ({
    id: c.CarrierId ?? c.Id,
    name: c.CarrierName ?? c.Name ?? "",
  }));

// build lane maps once
// - laneIdToDestination: LaneId -> Destination name
// - laneIdToSource: LaneId -> Source name
const buildLaneMaps = (lanes) => {
  const laneIdToDestination = {};
  const laneIdToSource = {};
  (lanes || []).forEach((l) => {
    laneIdToDestination[l.LaneId] = l.LaneDestination;
    laneIdToSource[l.LaneId] = l.LaneSource;
  });
  return { laneIdToDestination, laneIdToSource };
};

// generic group by
const groupQuotesBy = (quotes, keyFn) => {
  const map = new Map();
  (quotes || []).forEach((q) => {
    const key = keyFn(q);
    if (key == null) return;
    const arr = map.get(key) || [];
    arr.push(q);
    map.set(key, arr);
  });
  return map;
};

// Compute the best (top1) and N-th best distinct BidValue in a single pass.
// - This avoids sorting the whole array and building a Set of all values.
// - Complexity is O(n) per group instead of O(n log n).
const topFromQuotes = (quotesArr, nth) => {
  if (!Array.isArray(quotesArr) || quotesArr.length === 0) {
    return { top1: null, topN: null };
  }

  let first = Number.POSITIVE_INFINITY;
  let second = Number.POSITIVE_INFINITY;

  for (const q of quotesArr) {
    const raw = q?.BidValue ?? q?.bidValue;
    const v = Number(raw);
    if (!Number.isFinite(v)) continue;

    if (v < first) {
      // shift the previous best to second if it is different
      if (first !== Number.POSITIVE_INFINITY && v !== first) {
        second = first;
      }
      first = v;
    } else if (v > first && v < second) {
      // strictly greater than first but better than current second
      second = v;
    }
  }

  if (!Number.isFinite(first)) {
    return { top1: null, topN: null };
  }

  const top1 = first;

  let topN = top1;
  if (nth >= 2 && Number.isFinite(second)) {
    topN = second;
  }

  return { top1, topN };
};


// build finished tender model for the default dashboard view
const buildFinishedTenderModel = (quotes, lanes, carriers, nth = 2) => {
  const normCarriers = normalizeCarriers(carriers);
  const carrierMap = new Map(normCarriers.map((c) => [c.id, c]));
  const { laneIdToDestination, laneIdToSource } = buildLaneMaps(lanes);

  const quotesByLane = groupQuotesBy(quotes, (q) => q.LaneId);
  const quotesByDestination = groupQuotesBy(
    quotes,
    (q) => laneIdToDestination[q.LaneId]
  );
  const quotesBySource = groupQuotesBy(quotes, (q) => laneIdToSource[q.LaneId]);

  const topByLane = new Map();
  quotesByLane.forEach((arr, laneId) =>
    topByLane.set(laneId, topFromQuotes(arr, nth))
  );

  const topByDestination = new Map();
  quotesByDestination.forEach((arr, dest) =>
    topByDestination.set(dest, topFromQuotes(arr, nth))
  );

  const topBySource = new Map();
  quotesBySource.forEach((arr, src) =>
    topBySource.set(src, topFromQuotes(arr, nth))
  );

  return {
    nth,
    normCarriers,
    carrierMap,
    laneIdToDestination,
    laneIdToSource,
    quotesByLane,
    quotesByDestination,
    quotesBySource,
    topByLane,
    topByDestination,
    topBySource,
  };
};

// ===========================================================================================================================================
// Summary rows builder (pure)
// - Uses:
//     - hvAllLanes: ALL TenderLanes for tender (inactive already filtered server-side; but we stay defensive)
//     - hvLaneSummaries: lane-level aggregates from TenderQuotes/laneSummaries/accepted
// - Returns rows in the shape expected by TenderSummaryTable
// ===========================================================================================================================================

const buildSummaryLaneRows = ({ allLanes, laneSummaries, isSpotTender }) => {
  const lanesArr = Array.isArray(allLanes) ? allLanes : [];
  const summariesArr = Array.isArray(laneSummaries) ? laneSummaries : [];

  const laneIdOf = (l) => l?.LaneId ?? l?.laneId ?? l?.Id ?? l?.id ?? null;
  const laneOriginOf = (l) =>
    l?.LaneSource ??
    l?.laneSource ??
    l?.SourceLocation ??
    l?.sourceLocation ??
    "";
  const laneDestinationOf = (l) =>
    l?.LaneDestination ??
    l?.laneDestination ??
    l?.DestinationLocation ??
    l?.destinationLocation ??
    "";

  const laneById = new Map(
    lanesArr
      .map((l) => [laneIdOf(l), l])
      .filter(([id]) => id != null)
  );

  const summaryByLaneId = new Map(
    summariesArr
      .map((s) => [s?.LaneId ?? s?.laneId ?? null, s])
      .filter(([id]) => id != null)
  );

  const rows = [];
  laneById.forEach((lane, laneId) => {
    const origin = laneOriginOf(lane);
    const destination = laneDestinationOf(lane);
    const summary = summaryByLaneId.get(laneId) || {};

    const totalQuotes = summary.TotalQuotes ?? summary.totalQuotes ?? 0;
    const maxBidValue = summary.MaxBidValue ?? summary.maxBidValue ?? null;
    const minBidValue = summary.MinBidValue ?? summary.minBidValue ?? null;
    const totalLoads = summary.TotalLoads ?? summary.totalLoads ?? null;
    const maxLoads = summary.MaxLoads ?? summary.maxLoads ?? null;
    const minLoads = summary.MinLoads ?? summary.minLoads ?? null;
    const maxEstimatedTransitDays =
      summary.MaxEstimatedTransitDays ??
      summary.maxEstimatedTransitDays ??
      null;
    const minEstimatedTransitDays =
      summary.MinEstimatedTransitDays ??
      summary.minEstimatedTransitDays ??
      null;

    // IMPORTANT:
    // - Aggregate metrics (best Bid, ETD, Loads) come directly from laneSummaries (accepted + active quotes).
    // - TotalBidValue comes from TenderLanes (backend pre-aggregated value).
    //   We NEVER sum quotes in the frontend.
    const totalBidValue =
      lane?.TotalBidValue ??
      lane?.totalBidValue ??
      summary.TotalBidValue ??
      summary.totalBidValue ??
      null;

    const isSatisfied =
      lane?.IsSatisfied ??
      lane?.isSatisfied ??
      lane?.Satisfied ??
      lane?.satisfied ??
      null;

    const satisfiedAt = lane?.SatisfiedAt ?? lane?.satisfiedAt ?? null;

    const satisfiedInPhase =
      lane?.SatisfiedInPhase ??
      lane?.satisfiedInPhase ??
      lane?.SatisfiedInTenderPhase ??
      lane?.satisfiedInTenderPhase ??
      null;

    rows.push({
      laneId,
      origin,
      destination,
      totalQuotes,
      totalBidValue,
      maxBidValue,
      minBidValue,
      totalLoads,
      maxLoads,
      minLoads,
      maxEstimatedTransitDays,
      minEstimatedTransitDays,
      isSatisfied,
      satisfiedAt,
      // Only keep SatisfiedInPhase for Non-Spot
      satisfiedInPhase: isSpotTender ? null : satisfiedInPhase,
    });
  });

  rows.sort((a, b) => {
    const ao = String(a.origin || "").localeCompare(String(b.origin || ""));
    if (ao !== 0) return ao;
    return String(a.destination || "").localeCompare(String(b.destination || ""));
  });

  return rows;
};

// ===========================================================================================================================================
// Snapshot keys (persisted per tab namespace)
// ===========================================================================================================================================
const K = {
  owner: "TFIN.ownerTabId",
  operation: "TFIN.operation",
  sessionLine: "TFIN.sessionLine",
  activeView: "TFIN.activeView",

  // full datasets (to rehydrate instantly without hitting the network)
  tenderInfo: "TFIN.tenderInfo",
  quotes: "TFIN.quotes",
  lanes: "TFIN.lanes",
  laneSummaries: "TFIN.laneSummaries",
  carriers: "TFIN.carriers",

  // table metric selector (shared by lanes + carriers views)
  metricKey: "TFIN.metricKey",
};

// ===========================================================================================================================================
// Component
// ===========================================================================================================================================
export default function TenderFinishedPage({ handleTabClose, toastMessages }) {
  const { t } = useTranslation();
  const ctx = useContext(LoadingContext);
  const dispatch = useDispatch();
  const { renameTab } = useContext(NavigateContext);

  const selectedTab = useSelector(
    (s) => s.StateKeyValue.find((o) => o.key === "selectedTab")?.value
  );
  const rawTabsList = useSelector(
    (s) => s.StateKeyValue.find((o) => o.key === "tabs")?.value
  );
  const tabsList = useMemo(() => rawTabsList || [], [rawTabsList]);
  const globalOperation = useSelector(
    (s) => s.StateKeyValue.find((o) => o.key === "operation")?.value
  );
  const globalLine = useSelector(
    (s) => s.StateKeyValue.find((o) => o.key === "TenderLine")?.value
  );
  const stateKeyValue = useSelector((s) => s.StateKeyValue);

  const allStateKeys = useMemo(
    () => (stateKeyValue || []).map((o) => o.key),
    [stateKeyValue]
  );

  const currentNsKey = stateKeyFrom(selectedTab);

  const currentNsSnapshot = useMemo(() => {
    if (!currentNsKey) return [];
    const raw = stateKeyValue?.find((o) => o.key === currentNsKey)?.value;
    return Array.isArray(raw) ? raw : [];
  }, [stateKeyValue, currentNsKey]);

  const [id, setId] = useState(selectedTab);
  useEffect(() => {
    setId(selectedTab);
  }, [selectedTab]);

  const [operation, setOperation] = useState(globalOperation ?? "View");
  const [sessionLine, setSessionLine] = useState(globalLine ?? null);

  const [activeView, setActiveView] = useState("default"); // 'default' | 'lanes' | 'carriers' | 'summary'
  const [isFirst, setIsFirst] = useState(true);

  const [tenderInfo, setTenderInfo] = useState({});
  const [quotes, setQuotes] = useState([]);
  const [lanes, setLanes] = useState([]);
  const [laneSummaries, setLaneSummaries] = useState([]);
  const [carriers, setCarriers] = useState([]);

  const [metricKey, setMetricKey] = useState("bid");
  const metricOptions = useMemo(
    () => [
      { value: "bid", label: t("BidValue") },
      { value: "loads", label: t("Loads") },
      { value: "transit", label: t("EstimatedTransitDays") },
    ],
    [t]
  );
  const metricSelectOptions = useMemo(
    () =>
      metricOptions.map((o) => ({
        Id: o.value,
        value: o.value,
        label: o.label,
      })),
    [metricOptions]
  );

  const baseUrl = ctx.DEFAULTURL;
  const tenderService = useMemo(
    () => new BufferService("Tenders", baseUrl),
    [baseUrl]
  );
  const tenderQuotesService = useMemo(
    () => new BufferService("TenderQuotes", baseUrl),
    [baseUrl]
  );
  const tenderLanesService = useMemo(
    () => new BufferService("TenderLanes", baseUrl),
    [baseUrl]
  );
  const tenderDistinctCarriersService = useMemo(
    () => new BufferService("TenderQuotes/distinctCarriers", baseUrl),
    [baseUrl]
  );
  const tenderLaneSummariesService = useMemo(
    () => new BufferService("TenderQuotes/laneSummaries/accepted", baseUrl),
    [baseUrl]
  );

  const activeNsRef = useRef(null);
  const activeTidRef = useRef(null);
  const prevTabRef = useRef(null);
  const persistDebounceRef = useRef(null);
  const didGCRef = useRef(false);
  const hydratedOnceRef = useRef(false);
  const nsSnapshotRef = useRef([]);
  const rehydratingRef = useRef(false);

  useEffect(() => {
    if (currentNsKey && activeNsRef.current === currentNsKey) {
      nsSnapshotRef.current = Array.isArray(currentNsSnapshot)
        ? currentNsSnapshot
        : [];
    }
  }, [currentNsKey, currentNsSnapshot]);

  const setSession = useCallback(
    (nsKey, entries) => {
      if (!nsKey) return;

      const current =
        nsKey === activeNsRef.current ? nsSnapshotRef.current || [] : [];
      const map = new Map(current.map((e) => [e.key, e]));
      let changed = false;

      for (const kv of entries) {
        const prevVal = map.get(kv.key)?.value;
        if (!deepEq(prevVal, kv.value)) {
          changed = true;
          map.set(kv.key, kv);
        }
      }

      if (changed) {
        const nextArray = Array.from(map.values());
        dispatch(M_StateActions.addM_State({ key: nsKey, value: nextArray }));
        writeToStorage(nsKey, nextArray);
        if (nsKey === activeNsRef.current) nsSnapshotRef.current = nextArray;
      }
    },
    [dispatch]
  );

  const flushSnapshot = useCallback(
    (forTabId = null) => {
      const tid = forTabId ?? activeTidRef.current ?? id;
      const ns = stateKeyFrom(tid);
      if (!ns) return;

      const payload = [
        { key: K.owner, value: String(tid) },
        { key: K.operation, value: operation },
        { key: K.sessionLine, value: sessionLine },
        { key: K.activeView, value: activeView },
        { key: K.tenderInfo, value: tenderInfo || {} },
        { key: K.quotes, value: quotes || [] },
        { key: K.lanes, value: lanes || [] },
        { key: K.laneSummaries, value: laneSummaries || [] },
        { key: K.carriers, value: carriers || [] },
        { key: K.metricKey, value: metricKey },
      ];

      setSession(ns, payload);
    },
    [
      id,
      operation,
      sessionLine,
      activeView,
      tenderInfo,
      quotes,
      lanes,
      laneSummaries,
      carriers,
      metricKey,
      setSession,
    ]
  );

  useEffect(() => {
    if (didGCRef.current) return;
    if (!tabsList || tabsList.length === 0) return;
    didGCRef.current = true;

    try {
      const openIds = new Set((tabsList || []).map((t) => String(t.value)));
      (allStateKeys || [])
        .filter((k) => typeof k === "string" && k.endsWith("::TFIN"))
        .forEach((ns) => {
          const tid = ns.split("::")[0];
          if (!openIds.has(String(tid))) {
            dispatch(M_StateActions.removeM_State({ key: ns }));
            sessionStorage.removeItem(ssKey(ns));
          }
        });
    } catch {
      /* noop */
    }
  }, [tabsList, allStateKeys, dispatch]);

  useLayoutEffect(() => {
    const prev = prevTabRef.current;
    if (prev !== null && prev !== selectedTab) flushSnapshot(prev);

    if (!selectedTab) {
      prevTabRef.current = null;
      return;
    }

    const ns = stateKeyFrom(selectedTab);
    activeNsRef.current = ns;
    activeTidRef.current = selectedTab;

    let snap = Array.isArray(currentNsSnapshot) ? currentNsSnapshot : [];
    if (!snap.length) {
      const fromStore = readFromStorage(ns);
      if (Array.isArray(fromStore) && fromStore.length) {
        snap = fromStore;
        dispatch(M_StateActions.addM_State({ key: ns, value: snap }));
      }
    }

    rehydratingRef.current = true;

    const storedOwner = pickKV(snap, K.owner);
    const storedOp = pickKV(snap, K.operation);
    const storedLine = pickKV(snap, K.sessionLine);
    const storedView = pickKV(snap, K.activeView);
    const storedMetricKey = pickKV(snap, K.metricKey);

    const snapTender = pickKV(snap, K.tenderInfo);
    const snapQuotes = pickKV(snap, K.quotes);
    const snapLanes = pickKV(snap, K.lanes);
    const snapLaneSummaries = pickKV(snap, K.laneSummaries);
    const snapCarriers = pickKV(snap, K.carriers);

    if (storedOwner) {
      if (storedOp !== undefined) setOperation(storedOp);
      if (storedLine !== undefined) setSessionLine(storedLine);
      if (storedView !== undefined) setActiveView(storedView);
      if (storedMetricKey !== undefined) setMetricKey(storedMetricKey);

      if (snapTender) setTenderInfo(snapTender);
      if (Array.isArray(snapQuotes)) setQuotes(snapQuotes);
      if (Array.isArray(snapLanes)) setLanes(snapLanes);
      if (Array.isArray(snapLaneSummaries))
        setLaneSummaries(snapLaneSummaries);
      if (Array.isArray(snapCarriers)) setCarriers(snapCarriers);

      hydratedOnceRef.current = true;
      nsSnapshotRef.current = snap;
    } else {
      const seed = [
        { key: K.owner, value: String(selectedTab) },
        { key: K.operation, value: operation },
        { key: K.sessionLine, value: sessionLine },
        { key: K.activeView, value: activeView },
        { key: K.tenderInfo, value: {} },
        { key: K.quotes, value: [] },
        { key: K.lanes, value: [] },
        { key: K.laneSummaries, value: [] },
        { key: K.carriers, value: [] },
        { key: K.metricKey, value: metricKey },
      ];
      dispatch(M_StateActions.addM_State({ key: ns, value: seed }));
      writeToStorage(ns, seed);
      nsSnapshotRef.current = seed;
      hydratedOnceRef.current = false;
    }

    setTimeout(() => {
      rehydratingRef.current = false;
    }, 0);
    prevTabRef.current = selectedTab || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (persistDebounceRef.current) clearTimeout(persistDebounceRef.current);
      flushSnapshot();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      if (persistDebounceRef.current) clearTimeout(persistDebounceRef.current);
      flushSnapshot();
    };
  }, [flushSnapshot]);

  useEffect(() => {
    if (!activeNsRef.current || rehydratingRef.current) return;
    if (persistDebounceRef.current) clearTimeout(persistDebounceRef.current);
    persistDebounceRef.current = setTimeout(() => flushSnapshot(), 200);
    return () => {
      if (persistDebounceRef.current) clearTimeout(persistDebounceRef.current);
    };
  }, [
    operation,
    sessionLine,
    activeView,
    tenderInfo,
    quotes,
    lanes,
    laneSummaries,
    carriers,
    metricKey,
    flushSnapshot,
  ]);

  useEffect(() => {
    if (!isFirst) return;
    setIsFirst(false);

    dispatch(M_StateActions.removeM_State({ key: "operation" }));
    dispatch(M_StateActions.removeM_State({ key: "TenderLine" }));

    const ns = stateKeyFrom(id);
    if (ns) {
      const seed = [
        { key: K.owner, value: String(id) },
        { key: K.operation, value: operation },
        { key: K.sessionLine, value: sessionLine },
        { key: K.activeView, value: activeView },
        { key: K.tenderInfo, value: tenderInfo },
        { key: K.quotes, value: quotes },
        { key: K.lanes, value: lanes },
        { key: K.laneSummaries, value: laneSummaries },
        { key: K.carriers, value: carriers },
        { key: K.metricKey, value: metricKey },
      ];
      dispatch(M_StateActions.addM_State({ key: ns, value: seed }));
      writeToStorage(ns, seed);
      nsSnapshotRef.current = seed;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFirst]);

  useEffect(() => {
    if (!id || !renameTab) return;
    const base = t("TenderFinished");
    const name = tenderInfo?.Name || "";
    const label = name ? `${base} — ${name}` : base;
    renameTab(String(id), label);
  }, [id, tenderInfo?.Name, renameTab, t]);

  const setIsLoadRef = useRef(ctx.setIsLoad);
  useEffect(() => {
    setIsLoadRef.current = ctx.setIsLoad;
  }, [ctx.setIsLoad]);

  const loadAcceptedQuotesPaged = useCallback(
    async ({ tenderId }) => {
      if (!tenderId) {
        setQuotes([]);
        return [];
      }

      const pageSize = 200;
      let page = 1;
      const acc = [];
      const seen = new Set();

      const baseFilter = `TenderId eq ${tenderId} and IsAccepted eq true and Inactive eq false`;
      const qFilter = encodeURIComponent(baseFilter);

      while (true) {
        const res = await tenderQuotesService.filter(
          `?Page=${page}&PageSize=${pageSize}&filter=${qFilter}`
        );
        if (isFailurePayload(res)) break;

        const items = norm(res);
        if (!items.length) break;

        for (const it of items) {
          const id = it?.Id ?? it?.id;
          if (id != null && !seen.has(id)) {
            seen.add(id);
            acc.push(it);
          }
        }

        const totalPages = Number(
          res?.TotalPages ?? (items.length < pageSize ? page : page + 1)
        );
        if (page >= totalPages) break;
        page += 1;
      }

      const normalized = acc.map((q) => ({
        ...q,
        Id: q.Id ?? q.id,
        TenderId: q.TenderId ?? q.tenderId,
        TenderPhaseId: q.TenderPhaseId ?? q.tenderPhaseId,
        LaneId: q.LaneId ?? q.laneId,
        CarrierId: q.CarrierId ?? q.carrierId,
        BidValue: q.BidValue ?? q.bidValue,
        Loads: q.Loads ?? q.loads,
        EstimatedTransitDays: q.EstimatedTransitDays ?? q.estimatedTransitDays,
        Inactive: q.Inactive ?? q.inactive,
        IsAccepted: q.IsAccepted ?? q.isAccepted,
        AcceptedAt: q.AcceptedAt ?? q.acceptedAt,
        Carrier: q.Carrier ?? q.carrier,
        SourceLocation: q.SourceLocation ?? q.sourceLocation,
        DestinationLocation: q.DestinationLocation ?? q.destinationLocation,
      }));

      setQuotes(normalized);
      return normalized;
    },
    [tenderQuotesService]
  );

  const loadTenderLanesPaged = useCallback(
    async ({ tenderId }) => {
      if (!tenderId) {
        setLanes([]);
        return [];
      }

      const pageSize = 200;
      let page = 1;
      const acc = [];
      const seen = new Set();

      const filter = encodeURIComponent(
        `TenderId eq ${tenderId} and Inactive eq false`
      );

      while (true) {
        const res = await tenderLanesService.filter(
          `?Page=${page}&PageSize=${pageSize}&filter=${filter}`
        );
        if (isFailurePayload(res)) break;

        const items = norm(res);
        if (!items.length) break;

        for (const it of items) {
          const id = it?.Id ?? it?.LaneId ?? it?.laneId;
          if (id != null && !seen.has(id)) {
            seen.add(id);
            acc.push(it);
          }
        }

        const totalPages = Number(
          res?.TotalPages ?? (items.length < pageSize ? page : page + 1)
        );
        if (page >= totalPages) break;
        page += 1;
      }

      const normalized = acc.map((l) => ({
        ...l,
        Id: l.Id ?? l.id,
        LaneId: l.LaneId ?? l.laneId ?? l.Id ?? l.id,
        LaneSource:
          l.LaneSource ?? l.laneSource ?? l.SourceLocation ?? l.sourceLocation,
        LaneDestination:
          l.LaneDestination ??
          l.laneDestination ??
          l.DestinationLocation ??
          l.destinationLocation,
        Inactive: l.Inactive ?? l.inactive,
        IsActive:
          l.IsActive ??
            l.isActive ??
            (l.Inactive ?? l.inactive) != null
            ? !(l.Inactive ?? l.inactive)
            : true,
      }));

      setLanes(normalized);
      return normalized;
    },
    [tenderLanesService]
  );

  const loadDistinctCarriersPaged = useCallback(
    async ({ tenderId }) => {
      if (!tenderId) {
        setCarriers([]);
        return [];
      }

      const pageSize = 200;
      let page = 1;
      const acc = [];
      const seen = new Set();

      const filter = encodeURIComponent(`TenderId eq ${tenderId}`);

      while (true) {
        const res = await tenderDistinctCarriersService.filter(
          `?Page=${page}&PageSize=${pageSize}&Filter=${filter}`
        );
        if (isFailurePayload(res)) break;

        const items = norm(res);
        if (!items.length) break;

        for (const it of items) {
          const carrier = it?.Carrier ?? it?.carrier ?? it;
          const id = carrier?.Id ?? it?.CarrierId ?? it?.Id ?? it?.id;
          if (id != null && !seen.has(id)) {
            seen.add(id);
            acc.push({
              CarrierId: id,
              CarrierName:
                carrier?.Name ?? it?.CarrierName ?? it?.Name ?? "",
              Name: carrier?.Name ?? it?.CarrierName ?? it?.Name ?? "",
              QuotesCount: it?.QuotesCount ?? it?.quotesCount ?? null,
              QuotesAcceptedCount:
                it?.QuotesAcceptedCount ?? it?.quotesAcceptedCount ?? null,
            });
          }
        }

        const totalPages = Number(
          res?.TotalItems && pageSize
            ? Math.ceil(Number(res.TotalItems) / pageSize)
            : res?.TotalPages ?? (items.length < pageSize ? page : page + 1)
        );
        if (page >= totalPages) break;
        page += 1;
      }

      setCarriers(acc);
      return acc;
    },
    [tenderDistinctCarriersService]
  );

  const loadLaneSummariesPaged = useCallback(
    async ({ tenderId }) => {
      if (!tenderId) {
        setLaneSummaries([]);
        return [];
      }

      const pageSize = 200;
      let page = 1;
      const acc = [];
      const seen = new Set();

      const filter = encodeURIComponent(`TenderId eq ${tenderId}`);

      while (true) {
        const res = await tenderLaneSummariesService.filter(
          `?Page=${page}&PageSize=${pageSize}&Filter=${filter}`
        );
        if (isFailurePayload(res)) break;

        const items = norm(res);
        if (!items.length) break;

        for (const it of items) {
          const key = `${it?.TenderId ?? it?.tenderId ?? ""}::${it?.LaneId ?? it?.laneId ?? ""
            }`;
          if (!seen.has(key)) {
            seen.add(key);
            acc.push({
              ...it,
              TenderId: it?.TenderId ?? it?.tenderId,
              TenderPhaseId: it?.TenderPhaseId ?? it?.tenderPhaseId ?? null,
              LaneId: it?.LaneId ?? it?.laneId,
              TotalQuotes: it?.TotalQuotes ?? it?.totalQuotes ?? null,
              MaxBidValue: it?.MaxBidValue ?? it?.maxBidValue ?? null,
              MinBidValue: it?.MinBidValue ?? it?.minBidValue ?? null,
              TotalLoads: it?.TotalLoads ?? it?.totalLoads ?? null,
              MaxLoads: it?.MaxLoads ?? it?.maxLoads ?? null,
              MinLoads: it?.MinLoads ?? it?.minLoads ?? null,
              MaxEstimatedTransitDays:
                it?.MaxEstimatedTransitDays ??
                it?.maxEstimatedTransitDays ??
                null,
              MinEstimatedTransitDays:
                it?.MinEstimatedTransitDays ??
                it?.minEstimatedTransitDays ??
                null,
            });
          }
        }

        const totalPages = Number(
          res?.TotalItems && pageSize
            ? Math.ceil(Number(res.TotalItems) / pageSize)
            : res?.TotalPages ?? (items.length < pageSize ? page : page + 1)
        );
        if (page >= totalPages) break;
        page += 1;
      }

      setLaneSummaries(acc);
      return acc;
    },
    [tenderLaneSummariesService]
  );

  const onRefresh = useCallback(async () => {
    if (!sessionLine?.Id) return;
    setIsLoadRef.current?.(true);

    try {
      const tender = await tenderService.getId(sessionLine.Id);
      if (isFailurePayload(tender)) {
        toastMessages?.("error", "", apiMessage(tender, t("ErrorLoadingData")));
        setIsLoadRef.current?.(false);
        return;
      }
      setTenderInfo(tender);

      const lanesPromise = loadTenderLanesPaged({ tenderId: sessionLine.Id });
      const quotesPromise = loadAcceptedQuotesPaged({
        tenderId: sessionLine.Id,
      });
      const laneSummariesPromise = loadLaneSummariesPaged({
        tenderId: sessionLine.Id,
      });
      const carriersPromise = loadDistinctCarriersPaged({
        tenderId: sessionLine.Id,
      });

      await Promise.allSettled([
        lanesPromise,
        quotesPromise,
        laneSummariesPromise,
        carriersPromise,
      ]);

      hydratedOnceRef.current = true;
    } catch (err) {
      toastMessages?.("error", "", apiMessage(err, t("ErrorLoadingData")));
    } finally {
      setIsLoadRef.current?.(false);
    }
  }, [
    sessionLine?.Id,
    tenderService,
    loadTenderLanesPaged,
    loadAcceptedQuotesPaged,
    loadLaneSummariesPaged,
    loadDistinctCarriersPaged,
    toastMessages,
    t,
  ]);

  useEffect(() => {
    if (!sessionLine?.Id) return;
    onRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionLine?.Id]);

  const hvTender = useMemo(
    () =>
      Object.keys(tenderInfo || {}).length
        ? tenderInfo
        : pickKV(currentNsSnapshot, K.tenderInfo) || {},
    [tenderInfo, currentNsSnapshot]
  );

  const hvQuotes = useMemo(
    () =>
      quotes && quotes.length
        ? quotes
        : pickKV(currentNsSnapshot, K.quotes) || [],
    [quotes, currentNsSnapshot]
  );

  const hvLanes = useMemo(
    () =>
      lanes && lanes.length ? lanes : pickKV(currentNsSnapshot, K.lanes) || [],
    [lanes, currentNsSnapshot]
  );

  const hvCarriers = useMemo(
    () =>
      carriers && carriers.length
        ? carriers
        : pickKV(currentNsSnapshot, K.carriers) || [],
    [carriers, currentNsSnapshot]
  );

  const hvLaneSummaries = useMemo(
    () =>
      laneSummaries && laneSummaries.length
        ? laneSummaries
        : pickKV(currentNsSnapshot, K.laneSummaries) || [],
    [laneSummaries, currentNsSnapshot]
  );

  const hvAllLanes = useMemo(() => hvLanes || [], [hvLanes]);

  // Build lane best metric map from backend summaries.
  // - bestBidValue  -> MinBidValue (lower is better)
  // - bestLoadsValue -> MaxLoads (higher is better)
  // - bestEtdValue  -> MinEstimatedTransitDays (lower is better)
  const laneBestMap = useMemo(() => {
    const map = new Map();
    for (const s of hvLaneSummaries || []) {
      const laneIdRaw = s?.LaneId ?? s?.laneId;
      const laneId = laneIdRaw != null ? Number(laneIdRaw) : null;
      if (!Number.isFinite(laneId)) continue;

      const bestBidValue =
        s?.MinBidValue ?? s?.minBidValue ?? s?.BestBidValue ?? s?.bestBidValue;
      const bestLoadsValue =
        s?.MaxLoads ?? s?.maxLoads ?? s?.BestLoadsValue ?? s?.bestLoadsValue;
      const bestEtdValue =
        s?.MinEstimatedTransitDays ??
        s?.minEstimatedTransitDays ??
        s?.BestEtdValue ??
        s?.bestEtdValue;

      map.set(laneId, {
        bestBidValue:
          bestBidValue != null && Number.isFinite(Number(bestBidValue))
            ? Number(bestBidValue)
            : null,
        bestLoadsValue:
          bestLoadsValue != null && Number.isFinite(Number(bestLoadsValue))
            ? Number(bestLoadsValue)
            : null,
        bestEtdValue:
          bestEtdValue != null && Number.isFinite(Number(bestEtdValue))
            ? Number(bestEtdValue)
            : null,
      });
    }
    return map;
  }, [hvLaneSummaries]);

  const parseBool = (v) => {
    if (typeof v === "boolean") return v;
    if (typeof v === "number") return v !== 0;
    if (typeof v === "string") {
      const s = v.trim().toLowerCase();
      if (s === "true" || s === "1" || s === "yes") return true;
      if (s === "false" || s === "0" || s === "no" || s === "") return false;
    }
    return null;
  };

  const isSpotTender = (() => {
    const raw =
      hvTender?.IsSpotTender ??
      hvTender?.isSpotTender ??
      hvTender?.IsSpot ??
      hvTender?.isSpot;

    const parsed = parseBool(raw);
    if (parsed !== null) return parsed;

    const typeStr =
      hvTender?.TenderType ??
      hvTender?.tenderType ??
      hvTender?.Mode ??
      hvTender?.mode ??
      "";

    if (typeof typeStr !== "string") return false;

    const s = typeStr.trim().toLowerCase();
    const compact = s.replace(/[\s_-]+/g, " ");

    if (
      /\bnon\s*spot\b/.test(compact) ||
      /\bnonspot\b/.test(s.replace(/[\s_-]+/g, ""))
    ) {
      return false;
    }

    return /\bspot\b/.test(compact);
  })();

  const summaryLaneRows = useMemo(
    () =>
      buildSummaryLaneRows({
        allLanes: hvAllLanes,
        laneSummaries: hvLaneSummaries,
        isSpotTender,
      }),
    [hvAllLanes, hvLaneSummaries, isSpotTender]
  );

  const DEFAULT_NTH = 2;
  const defaultModel = useMemo(
    () => buildFinishedTenderModel(hvQuotes, hvLanes, hvCarriers, DEFAULT_NTH),
    [hvQuotes, hvLanes, hvCarriers]
  );

  return (
    <>
      <MHeader
        element={
          <>
            <MButton
              icon={
                <i
                  className="pi pi-table iconTable"
                  style={{ color: "var(--color-green)" }}
                />
              }
              label={t("ViewDefault")}
              onClick={() => setActiveView("default")}
            />

            <MButton
              tooltip={t("Summary") || "Summary"}
              icon={
                <i
                  className="pi pi-chart-bar iconTable"
                  style={{ color: "var(--color-green-light)" }}
                />
              }
              onClick={() => setActiveView("summary")}
              label={t("ViewSummary") || "Summary"}
            />

            <MButton
              icon={
                <i
                  className="pi pi-arrow-right-arrow-left iconTable"
                  style={{ color: "var(--color-green)" }}
                />
              }
              label={t("ViewLanes")}
              onClick={() => setActiveView("lanes")}
            />

            <MButton
              icon={
                <i
                  className="pi pi-truck iconTable"
                  style={{ color: "var(--color-green)" }}
                />
              }
              label={t("ViewCarriers")}
              onClick={() => setActiveView("carriers")}
            />
          </>
        }
      />

      <div className="firstDivContainer tender-finished-container">
        {activeView === "default" && (
          <div className="tender-view-section">
            <span className="tender-view-title">{t("ViewDefault")}</span>

            <TopsByCarrierTable model={defaultModel} />

            <TopsByDestinationTable model={defaultModel} top={1} />

            <TopsByDestinationTable model={defaultModel} top={2} />

            <TopsBySourceTable model={defaultModel} top={1} />

            <TopsBySourceTable model={defaultModel} top={2} />
          </div>
        )}

        {activeView === "summary" && (
          <div className="tender-view-section tender-view-section--scroll">
            <div className="tender-view-header">
              <span className="tender-view-title">
                {t("ViewSummary") || "Summary"}
              </span>
            </div>

            <TenderSummaryTable
              tenderInfo={hvTender}
              rows={summaryLaneRows}
              isSpotTender={isSpotTender}
              onRefresh={onRefresh}
            />
          </div>
        )}

        {/* Lanes view (inline) */}
        {activeView === "lanes" && (
          <div className="tender-view-section tender-view-section--scroll">
            <div className="tender-view-header">
              <span className="tender-view-title">{t("ViewLanes")}</span>
            </div>

            {/* Shared metric selector (used by lanes + carriers views) */}
            <div className="tender-metric-select-row">
              <div>
                <div className="tender-metric-select">
                  <MSelect
                    label={t("Best")}
                    options={metricSelectOptions}
                    value={metricKey}
                    noNone
                    autocomplete={false}
                    onChange={(e) => {
                      const nextVal = e?.target?.value || "bid";
                      setMetricKey(nextVal);
                    }}
                  />
                </div>
              </div>
            </div>

            <ViewLaneTable
              acceptedQuotes={hvQuotes || []}
              carriers={hvCarriers || []}
              lanes={hvLanes || []}
              tenderInfo={hvTender}
              onRefresh={onRefresh}
              isFinished={true}
              isSpotTender={isSpotTender}
              currentPhaseId={null}
              // metricKey controls which metric is used to select the "best" quote per lane: "bid" | "loads" | "transit"
              metricKey={metricKey}
              // laneBestMap provides BEST metrics per lane from backend summaries (no FE aggregation on big datasets)
              laneBestMap={laneBestMap}
              // toggle infra is still wired, but with isFinished=true no checkbox is interactive
              acceptBusyIds={undefined}
              onSetQuoteAccepted={undefined}
            />
          </div>
        )}

        {activeView === "carriers" && (
          <div className="tender-view-section tender-view-section--scroll">
            <div className="tender-view-header">
              <span className="tender-view-title">{t("ViewCarriers")}</span>
            </div>

            <div className="tender-metric-select-row">
              <div>
                <div className="tender-metric-select">
                  <MSelect
                    label={t("Best")}
                    options={metricSelectOptions}
                    value={metricKey}
                    noNone
                    autocomplete={false}
                    onChange={(e) => {
                      const nextVal = e?.target?.value || "bid";
                      setMetricKey(nextVal);
                    }}
                  />
                </div>
              </div>
            </div>

            <CarrierByLaneTable
              tenderInfo={hvTender}
              quotes={hvQuotes || []}
              lanes={hvLanes || []}
              carriers={hvCarriers || []}
              onRefresh={onRefresh}
              metricKey={metricKey}
              laneBestMap={laneBestMap}
            />
          </div>
        )}
      </div>
    </>
  );
}

TenderFinishedPage.propTypes = {
  handleTabClose: PropTypes.func,
  toastMessages: PropTypes.func,
};
